import React, { useState, useRef, useCallback } from 'react';
import { CornerDownLeft } from 'lucide-react';


export interface Option {
  label: string;
  description: string;
}

interface ChoiceQuestionProps {
  question: string;
  options: Option[];
  onSelect: (value: string) => void;
}

function OptionRow({
  option,
  isSelected,
  tabIndex,
  onClick,
}: {
  option: Option;
  isSelected: boolean;
  tabIndex: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      tabIndex={tabIndex}
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border rounded-lg mb-2 transition-all outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 [-webkit-tap-highlight-color:transparent] ${
        isSelected
          ? 'bg-sky-50 border-sky-300'
          : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
      }`}
    >
      <p className="text-sm font-semibold text-neutral-900 leading-snug">{option.label}</p>
      {isSelected && option.description && (
        <p className="text-xs text-neutral-500 mt-1.5 leading-relaxed">{option.description}</p>
      )}
    </button>
  );
}

const ChoiceQuestion: React.FC<ChoiceQuestionProps> = ({ question, options, onSelect }) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [customText, setCustomText] = useState('');
  const [otherFocused, setOtherFocused] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const hasCalledRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const totalOptions = options.length + 1; // +1 for custom

  const commit = useCallback(
    (value: string) => {
      if (hasCalledRef.current) return;
      hasCalledRef.current = true;
      onSelect(value);
    },
    [onSelect]
  );

  const handleOptionClick = (value: string) => {
    setSelectedValue(value);
    setCustomText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, totalOptions - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (focusedIndex === options.length) return; // Other row 的 Enter 由 input 自己處理
      const option = options[focusedIndex];
      if (option) {
        setSelectedValue(option.label);
        setCustomText('');
      }
    }
  };

  const pendingValue = customText.trim() !== '' ? customText.trim() : (otherFocused ? null : selectedValue);
  const canSubmit = pendingValue !== null && pendingValue !== '';

  return (
    <div
      role="radiogroup"
      aria-label={question}
      onKeyDown={handleKeyDown}
      className="px-4 pt-4 pb-safe"
    >
      <p className="text-sm font-semibold text-neutral-900 mb-3">{question}</p>

      {options.map((option, index) => (
        <OptionRow
          key={option.label}
          option={option}
          isSelected={selectedValue === option.label && customText.trim() === ''}
          tabIndex={focusedIndex === index ? 0 : -1}
          onClick={() => handleOptionClick(option.label)}
        />
      ))}

      {/* Other — 永遠是最後一個選項，外觀與其他選項一致 */}
      <div
        onClick={() => { setSelectedValue(null); inputRef.current?.focus(); }}
        className={`px-4 py-3 border rounded-lg mb-3 transition-all cursor-text ${
          otherFocused || customText.trim() !== ''
            ? 'bg-sky-50 border-sky-300'
            : 'border-neutral-200 hover:border-neutral-300'
        }`}
      >
        <p className="text-sm font-semibold text-neutral-900 leading-snug">Other</p>
        <div className="flex items-center gap-1 mt-0.5">
          <input
            ref={inputRef}
            type="text"
            placeholder="Type your answer…"
            value={customText}
            onFocus={() => { setOtherFocused(true); setSelectedValue(null); }}
            onBlur={() => setOtherFocused(false)}
            onChange={e => setCustomText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && customText.trim()) {
                e.stopPropagation();
                commit(customText.trim());
              }
            }}
            className="flex-1 text-xs bg-transparent focus:outline-none placeholder:text-neutral-400 text-neutral-500 leading-relaxed"
            tabIndex={focusedIndex === totalOptions - 1 ? 0 : -1}
            aria-label="Something else"
          />
          {customText.trim() && (
            <button
              type="button"
              onClick={() => commit(customText.trim())}
              className="p-1 text-neutral-400 hover:text-neutral-700 transition-colors"
              aria-label="Submit"
            >
              <CornerDownLeft className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* 底部送出按鈕 */}
      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => { if (pendingValue) commit(pendingValue); }}
        className="btn-primary btn-sm w-full"
      >
        Continue
      </button>
    </div>
  );
};

export default ChoiceQuestion;
